import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Bot,
  ArrowRightToLine,
  ArrowLeftToLine,
  Video,
  MessageCircle,
  Target,
  BarChart3,
  DollarSign,
  Settings,
  Settings2,
  Repeat,
  RotateCcw,
  Smartphone,
  UserCog,
  FileText,
  Database,
  Shield,
  Dumbbell,
  Apple,
  BookOpen,
  Sparkles,
  Zap,
  Activity,
  Camera,
  ChevronDown,
  Upload,
  FolderOpen,
  Gauge,
  HeartPulse,
  UserPlus,
  ArrowLeftRight,
  Banknote,
  Building2,
} from 'lucide-react';
import { getSidebarPreferences } from '../utils/sidebarPreferences';
import SidebarCustomizer from './SidebarCustomizer';
import { getUser } from '../utils/helpers';
import { ROLE_TYPES, migrateRoleName, ROLE_HIERARCHY } from '../config/roleConfig';
import { sidebarStyles } from '../tokens/sidebar-styles';
import { C } from './design-system/primitives/constants';
import axios from 'axios';
import config from '../utils/config';

/**
 * Sport-specific navigation configuration
 * Icons-only sidebar — labels shown on hover via title attribute
 */
/**
 * Generate standard sidebar sections for any sport.
 * All sports get the same 5-section structure with sport-specific paths.
 */
function makeSportSections(sport, extras = {}) {
  const p = `/${sport}`;
  return [
    {
      id: 'intelligence',
      label: 'INTELLIGENCE',
      items: [
        { id: 'slamAI', label: 'Ask SLAM AI', icon: <MessageCircle size={18} />, path: `${p}/ask-slam-ai` },
        { id: 'canvas', label: 'Agent Builder', icon: <Bot size={18} />, path: `${p}/canvas` },
        ...(extras.scoutingItems || []),
      ],
    },
    {
      id: 'frontOffice',
      label: 'FRONT OFFICE',
      items: [
        { id: 'gmDashboard', label: 'Dashboard', icon: <BarChart3 size={18} />, path: `${p}/gm` },
        { id: 'capSheet', label: 'Cap Sheet', icon: <DollarSign size={18} />, path: `${p}/gm/cap-sheet` },
        { id: 'portalIntel', label: 'Portal Intel', icon: <Target size={18} />, path: `${p}/gm/portal` },
        { id: 'compliance', label: 'Compliance', icon: <Shield size={18} />, path: `${p}/gm/compliance` },
        { id: 'recruiting', label: 'Recruiting', icon: <UserPlus size={18} />, path: `${p}/recruiting` },
        { id: 'tradeTargets', label: 'Trade Targets', icon: <ArrowLeftRight size={18} />, path: `${p}/trade-targets` },
        { id: 'nilDashboard', label: 'NIL Dashboard', icon: <Banknote size={18} />, path: `${p}/nil-dashboard` },
        ...(extras.acquisitionItems || []),
      ],
    },
    {
      id: 'playerDevelopment',
      label: 'PLAYER DEVELOPMENT',
      items: [
        { id: 'videoHub', label: 'Video Hub', icon: <Video size={18} />, path: `${p}/video` },
        { id: 'dropZone', label: 'Drop Zone', icon: <Upload size={18} />, path: `${p}/drop-zone` },
        { id: 'myLibrary', label: 'My Library', icon: <FolderOpen size={18} />, path: `${p}/my-library` },
        { id: 'mobileApp', label: 'Mobile App', icon: <Smartphone size={18} />, path: `${p}/mobile-app` },
        { id: 'loadManagement', label: 'Load Management', icon: <Gauge size={18} />, path: `${p}/load-management` },
        { id: 'biometrics', label: 'Biometrics', icon: <HeartPulse size={18} />, path: `${p}/biometrics` },
        ...(extras.devItems || []),
      ],
    },
    {
      id: 'performance',
      label: 'PERFORMANCE',
      items: [
        { id: 'workoutGenerator', label: 'Workout Generator', icon: <Dumbbell size={18} />, path: `${p}/workout-generator` },
        { id: 'aiCoach', label: 'AI Coach', icon: <Sparkles size={18} />, path: `${p}/ai-coach` },
        { id: 'nutrition', label: 'Nutrition', icon: <Apple size={18} />, path: `${p}/nutrition` },
        { id: 'journal', label: 'Smart Journal', icon: <BookOpen size={18} />, path: `${p}/journal` },
        { id: 'crossfitWOD', label: 'WOD Generator', icon: <Zap size={18} />, path: `${p}/crossfit-generator` },
        { id: 'workoutHistory', label: 'Workout History', icon: <Activity size={18} />, path: `${p}/workout-history` },
        { id: 'movementAnalysis', label: 'Movement Analysis', icon: <Camera size={18} />, path: `${p}/movement-analysis` },
        { id: 'strengthAdmin', label: 'S&C Admin', icon: <Shield size={18} />, path: `${p}/strength-admin`, adminOnly: true },
      ],
    },
    {
      id: 'system',
      label: 'SYSTEM',
      items: [
        { id: 'reports', label: 'Reports', icon: <FileText size={18} />, path: `${p}/reports` },
        { id: 'integrations', label: 'Integrations', icon: <Database size={18} />, path: `${p}/integrations` },
        { id: 'settings', label: 'Settings', icon: <Settings size={18} />, path: `${p}/settings` },
        { id: 'userManagement', label: 'User Management', icon: <UserCog size={18} />, path: `${p}/user-management` },
      ],
    },
  ];
}

/**
 * Baseball-specific sidebar — trimmed for college baseball (UNC).
 * Removes items irrelevant to college: Cap Sheet, Trade Targets, Compliance,
 * Load Management, Biometrics, Mobile App, WOD Generator, etc.
 */
function makeBaseballSections() {
  const p = '/baseball';
  return [
    {
      id: 'frontOffice',
      label: 'FRONT OFFICE',
      items: [
        { id: 'gmDashboard', label: 'GM Dashboard', icon: <BarChart3 size={18} />, path: `${p}/gm` },
      ],
    },
    {
      id: 'performance',
      label: 'PERFORMANCE',
      items: [
        { id: 'workoutGenerator', label: 'Workout Generator', icon: <Dumbbell size={18} />, path: `${p}/workout-generator` },
      ],
    },
    {
      id: 'system',
      label: 'SYSTEM',
      items: [
        { id: 'settings', label: 'Settings', icon: <Settings size={18} />, path: `${p}/settings` },
      ],
    },
  ];
}

const SPORT_CONFIG = {
  basketball: {
    name: 'Basketball',
    emoji: '\u{1F3C0}',
    routePrefix: '/basketball',
    sections: makeSportSections('basketball', {
      devItems: [
        { id: 'biV2Matrix', label: 'BIv2 Shot Matrix', icon: <Target size={18} />, path: '/basketball/bi-v2' },
      ],
      scoutingItems: [
        { id: 'floridaHouston', label: 'Florida vs Houston', icon: <span style={{ fontSize: 16 }}>{'\u{1F3C6}'}</span>, path: '/basketball/florida-houston' },
        { id: 'realMadrid', label: 'Real Madrid', icon: <span style={{ fontSize: 16 }}>{'\u{1F402}'}</span>, path: '/basketball/real-madrid' },
        { id: 'brownHarvard', label: 'Brown vs Harvard', icon: <span style={{ fontSize: 16 }}>{'\u{1F43B}'}</span>, path: '/basketball/brown-harvard' },
      ],
    }),
  },
  baseball: {
    name: 'Baseball',
    emoji: '\u{26BE}',
    routePrefix: '/baseball',
    sections: makeBaseballSections(),
  },
  golf: {
    name: 'Golf',
    emoji: '\u{26F3}',
    routePrefix: '/golf',
    sections: makeSportSections('golf'),
  },
  soccer: {
    name: 'Soccer',
    emoji: '\u{26BD}',
    routePrefix: '/soccer',
    sections: makeSportSections('soccer'),
  },
  hockey: {
    name: 'Hockey',
    emoji: '\u{1F3D2}',
    routePrefix: '/hockey',
    sections: makeSportSections('hockey'),
  },
  football: {
    name: 'Football',
    emoji: '\u{1F3C8}',
    routePrefix: '/football',
    sections: makeSportSections('football'),
  },
};

/**
 * UnifiedSidebar - Clean icon-only navigation sidebar for all sports
 *
 * Features:
 * - Icons only — no text labels (hover for tooltip)
 * - Sport emoji at top left
 * - Thin dividers between sections
 * - Expandable to show labels
 */
export default function UnifiedSidebar({
  sport = 'basketball',
  isSidebarOpen,
  setIsSidebarOpen,
}) {
  const location = useLocation();
  const navigate = useNavigate();

  // Read active workspace from localStorage (set by WorkspaceProvider)
  const [activeWorkspaceName, setActiveWorkspaceName] = useState(null);
  useEffect(() => {
    const wsId = localStorage.getItem('slam_current_workspace_id');
    if (wsId) {
      fetch(`/api/workspaces/${wsId}`)
        .then((r) => r.ok ? r.json() : null)
        .then((data) => {
          if (data?.workspace?.teamName) {
            setActiveWorkspaceName(data.workspace.teamName);
          }
        })
        .catch(() => {});
    }
  }, []);

  const sportConfig = SPORT_CONFIG[sport] || SPORT_CONFIG.basketball;

  // Load expanded sections from localStorage (all expanded by default)
  const [expandedSections, setExpandedSections] = useState(() => {
    try {
      const stored = localStorage.getItem(`slam-sidebar-expanded:${sport}`);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) return new Set(parsed);
      }
    } catch { /* ignore */ }
    return new Set(sportConfig.sections.map(s => s.id));
  });

  // Customizer modal state
  const [isCustomizerOpen, setIsCustomizerOpen] = useState(false);

  // Sidebar preferences state
  const [sidebarPrefs, setSidebarPrefs] = useState(() => getSidebarPreferences(sport, sportConfig.sections));

  // Listen for preference changes
  useEffect(() => {
    const handlePrefsChange = (event) => {
      const eventSport = event?.detail?.sport;
      if (eventSport && eventSport !== sport) {
        return;
      }
      setSidebarPrefs(getSidebarPreferences(sport, sportConfig.sections));
    };
    window.addEventListener('sidebarPreferencesChanged', handlePrefsChange);
    return () => window.removeEventListener('sidebarPreferencesChanged', handlePrefsChange);
  }, [sport, sportConfig.sections]);

  useEffect(() => {
    setSidebarPrefs(getSidebarPreferences(sport, sportConfig.sections));
    try {
      const stored = localStorage.getItem(`slam-sidebar-expanded:${sport}`);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setExpandedSections(new Set(parsed));
          return;
        }
      }
    } catch { /* ignore */ }
    setExpandedSections(new Set(sportConfig.sections.map((section) => section.id)));
  }, [sport, sportConfig.sections]);

  // Get user role for admin-only filtering
  const userRole = useMemo(() => {
    const user = getUser();
    if (!user?.role) return null;
    if (!ROLE_HIERARCHY[user.role]) return migrateRoleName(user.role);
    return user.role;
  }, []);

  const isAdmin = userRole === ROLE_TYPES.ADMIN;

  // Apply preferences: filter hidden/admin items, then sort sections and items by saved order.
  const filteredNavSections = useMemo(() => {
    const defaultSectionOrder = sportConfig.sections.map((section) => section.id);
    const sectionOrder = sidebarPrefs.sectionOrder.length > 0
      ? sidebarPrefs.sectionOrder
      : defaultSectionOrder;
    const sectionOrderMap = new Map(sectionOrder.map((sectionId, index) => [sectionId, index]));
    const allItems = sportConfig.sections.flatMap((section) => section.items || []);
    const itemById = new Map(allItems.map((item) => [item.id, item]));

    return sportConfig.sections
      .map(section => {
        const defaultSectionItems = section.items.map((item) => item.id);
        const sectionItemOrder = sidebarPrefs.itemOrder?.[section.id]?.length
          ? sidebarPrefs.itemOrder[section.id]
          : defaultSectionItems;
        const sortedItems = sectionItemOrder
          .map((itemId) => itemById.get(itemId))
          .filter(Boolean)
          .filter((item) => {
            if (sidebarPrefs.hiddenItems.includes(item.id)) return false;
            if (item.adminOnly && !isAdmin) return false;
            return true;
          });

        return {
          ...section,
          items: sortedItems,
        };
      })
      .filter(section => section.items.length > 0)
      .sort((a, b) => {
        const indexA = sectionOrderMap.has(a.id) ? sectionOrderMap.get(a.id) : 999;
        const indexB = sectionOrderMap.has(b.id) ? sectionOrderMap.get(b.id) : 999;
        return indexA - indexB;
      });
  }, [sportConfig.sections, sidebarPrefs, isAdmin]);

  // Toggle section expansion (persisted to localStorage)
  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      try {
        localStorage.setItem(`slam-sidebar-expanded:${sport}`, JSON.stringify([...newSet]));
      } catch { /* ignore */ }
      return newSet;
    });
  };

  // All registered item paths — used to detect prefix collisions
  const allItemPaths = useMemo(
    () => filteredNavSections.flatMap((s) => s.items.map((i) => i.path)),
    [filteredNavSections]
  );

  // Check if item is active
  const isItemActive = (path) => {
    if (location.pathname === path) return true;
    if (!location.pathname.startsWith(path + '/')) return false;
    const moreSpecificMatch = allItemPaths.some(
      (p) =>
        p !== path &&
        p.startsWith(path + '/') &&
        (location.pathname === p || location.pathname.startsWith(p + '/'))
    );
    return !moreSpecificMatch;
  };

  // Handle navigation
  const handleNavClick = (item) => {
    if (item.collapseSidebar) {
      setIsSidebarOpen(false);
    }
    navigate(item.path);
  };

  return (
    <aside
      className={`
        ${isSidebarOpen ? 'w-[200px]' : 'w-[56px]'}
        transition-all duration-300 ease-in-out
        border-r border-[#1A1A1A] flex flex-col h-full bg-black
      `}
    >
      {/* Header — sport emoji + collapse toggle */}
      <div className={`border-b border-[#1A1A1A] ${isSidebarOpen ? 'p-3' : 'p-2'}`}>
        {isSidebarOpen ? (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate(`${sportConfig.routePrefix}/canvas`)}
                className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                title={`${sportConfig.name} Agent Builder`}
              >
                <span className="text-[22px] leading-none">{sportConfig.emoji}</span>
                <span style={{ color: C.text, fontWeight: 300, fontSize: 13, letterSpacing: 1 }}>
                  SLAM
                </span>
              </button>
              {sport !== 'baseball' && (
                <Link
                  to="/select-sport"
                  className="p-1.5 rounded-[8px] transition-colors text-[#555555] hover:bg-[rgba(255,255,255,0.04)] hover:text-[#999999]"
                  title="Switch Sport"
                >
                  <Repeat size={14} />
                </Link>
              )}
            </div>
            <button
              onClick={() => setIsSidebarOpen(false)}
              className="p-2 rounded-[10px] transition-colors hover:bg-[rgba(255,255,255,0.04)] text-[#999999]"
              title="Collapse sidebar"
            >
              <ArrowLeftToLine size={16} />
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <button
              onClick={() => navigate(`${sportConfig.routePrefix}/canvas`)}
              className="hover:opacity-80 transition-opacity"
              title={`${sportConfig.name} Agent Builder`}
            >
              <span className="text-[26px] leading-none">{sportConfig.emoji}</span>
            </button>
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="p-2 rounded-[10px] transition-colors hover:bg-[rgba(255,255,255,0.04)] text-[#999999]"
              title="Expand sidebar"
            >
              <ArrowRightToLine size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Workspace indicator */}
      {activeWorkspaceName && isSidebarOpen && (
        <div className="px-3 py-2 border-b border-[#1A1A1A]">
          <div className="flex items-center gap-2 text-[11px] text-[#666]">
            <Building2 size={12} className="text-[#7BAFD4]" />
            <span className="truncate font-medium" style={{ color: '#7BAFD4' }}>
              {activeWorkspaceName}
            </span>
          </div>
        </div>
      )}

      {/* Navigation with section headers */}
      <nav className="flex-1 overflow-y-auto py-2">
        {filteredNavSections.map((section, sectionIdx) => (
          <div key={section.id}>
            {/* Thin divider between sections */}
            {sectionIdx > 0 && (
              <div className="mx-3 my-2 border-t border-[#1A1A1A]" />
            )}

            {/* Section Header — visible when sidebar is expanded */}
            {isSidebarOpen && (
              <button
                onClick={() => toggleSection(section.id)}
                className={sidebarStyles.sectionHeader()}
                aria-expanded={expandedSections.has(section.id)}
                aria-controls={`section-${section.id}`}
              >
                <span>{section.label}</span>
                <ChevronDown
                  size={12}
                  className={sidebarStyles.chevron(expandedSections.has(section.id))}
                />
              </button>
            )}

            {/* Section Items */}
            {(expandedSections.has(section.id) || !isSidebarOpen) && (
              <div id={`section-${section.id}`} role="region" aria-label={section.label}>
                {section.items.map((item) => {
                  const isActive = isItemActive(item.path);
                  return (
                    <button
                      key={item.id}
                      onClick={() => handleNavClick(item)}
                      title={item.label}
                      className={sidebarStyles.navItemUnified(true, isActive, isSidebarOpen)}
                    >
                      <span className={sidebarStyles.iconUnified(isActive)}>
                        {item.icon}
                      </span>
                      {isSidebarOpen && (
                        <span className="text-sm font-medium truncate">{item.label}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </nav>

      {/* Footer - Onboarding + Customize */}
      <div className="p-2 border-t border-[#1A1A1A] space-y-2">
        <button
          onClick={async () => {
            const user = getUser();
            const userId = user?.id;
            if (!userId) return;
            try {
              await axios.post(`${config.SERVER_BASE_URL}/api/gamification/reset`, {}, {
                headers: { 'x-user-id': userId },
              });
            } catch { /* continue anyway */ }
            sessionStorage.removeItem('slam_gamification_cache');
            localStorage.removeItem('slam_sport_selected');
            localStorage.removeItem('slam_sport_selection');
            const tourKeys = Object.keys(localStorage).filter(k => k.startsWith('slam_tour_'));
            tourKeys.forEach(k => localStorage.removeItem(k));
            navigate('/draft-day');
          }}
          className={`flex items-center gap-2 rounded-[10px] transition-all hover:bg-[rgba(255,255,255,0.04)] ${isSidebarOpen ? 'w-full text-sm px-3 py-2' : 'p-2 justify-center'}`}
          style={{ color: C.t3 }}
          title="Replay Onboarding"
        >
          <RotateCcw size={16} />
          {isSidebarOpen && <span>Replay Onboarding</span>}
        </button>
        <button
          onClick={() => setIsCustomizerOpen(true)}
          className={`flex items-center gap-2 rounded-[10px] transition-all hover:bg-[rgba(123,175,212,0.08)] ${isSidebarOpen ? 'w-full text-sm px-3 py-2' : 'p-2 justify-center'}`}
          style={{
            color: C.gold,
            border: `1px solid ${C.goldDim}`,
          }}
          title="Customize"
        >
          <Settings2 size={16} />
          {isSidebarOpen && <span>Customize</span>}
        </button>
      </div>

      {/* Sidebar Customizer Modal */}
      <SidebarCustomizer
        isOpen={isCustomizerOpen}
        onClose={() => setIsCustomizerOpen(false)}
        navSections={sportConfig.sections}
        sport={sport}
        isAdmin={isAdmin}
      />
    </aside>
  );
}
